import React, { useState, useEffect, useMemo } from 'react';
import { Platform } from 'react-native';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { YStack, Spinner, SizableText, Card } from 'tamagui';
import { resolveComponent, getCompatibleComponents } from '@registry/componentResolver';
import apis from '@api/api';

interface SpecDrivenIntegrationRendererProps {
  integrationId: string;
  version?: string; // Optional: use specific spec version
  onComponentResolved?: (endpoint: string, component: React.ComponentType<any>) => void;
}

export const SpecDrivenIntegrationRenderer: React.FC<SpecDrivenIntegrationRendererProps> = ({
  integrationId,
  version,
  onComponentResolved
}) => {
  const queryClient = useQueryClient();

  // Fetch enriched spec (with version support)
  // __DEV__: For test integration, use mock data instead of API call
  const { data: spec, isLoading, error } = useQuery({
    queryKey: ['enrichedSpec', integrationId, version],
    queryFn: () => {
      // __DEV__: Return mock data for test integration
      if (__DEV__ && integrationId === "__test_spec_driven__") {
        return Promise.resolve({
          enrichedSpec: {
            openapi: "3.0.1",
            info: { title: "Test Integration", version: "1.0.0" },
            paths: {
              "/profile": {
                get: {
                  summary: "Get user profile",
                  "x-ui": {
                    capability: "read_profile",
                    renders: "card",
                    priority: 10,
                    surface: "options"
                  },
                  responses: {
                    "200": {
                      description: "Profile",
                      content: {
                        "application/json": {
                          schema: {
                            type: "object",
                            properties: {
                              id: { type: "string" },
                              name: { type: "string" },
                              image: { type: "string" },
                              email: { type: "string", format: "email" },
                              title: { type: "string" },
                              bio: { type: "string" }
                            },
                            required: ["id", "name"]
                          }
                        }
                      }
                    }
                  }
                }
              },
              "/analytics/summary": {
                get: {
                  summary: "Get analytics summary",
                  "x-ui": {
                    capability: "read_analytics",
                    renders: "card",
                    priority: 8,
                    surface: "dashboard"
                  },
                  responses: {
                    "200": {
                      description: "Summary",
                      content: {
                        "application/json": {
                          schema: {
                            type: "object",
                            properties: {
                              total: { type: "number" },
                              change: { type: "number" },
                              period: { type: "string" },
                              metrics: { type: "object" }
                            },
                            required: ["total"]
                          }
                        }
                      }
                    }
                  }
                }
              },
              "/posts": {
                post: {
                  summary: "Create post",
                  "x-ui": {
                    capability: "create_post",
                    renders: "form",
                    priority: 6,
                    surface: "options"
                  },
                  requestBody: {
                    required: true,
                    content: {
                      "application/json": {
                        schema: {
                          type: "object",
                          properties: {
                            content: { type: "string" },
                            media: { type: "array", items: { type: "string" } },
                            visibility: { type: "string", enum: ["public", "private"] }
                          },
                          required: ["content"]
                        }
                      }
                    }
                  },
                  responses: {
                    "201": { description: "Created" }
                  }
                }
              }
            }
          },
          version: "1.0.0",
          status: "enriched",
          endpointVersions: {},
          enrichmentMetadata: {}
        });
      }
      return apis.getEnrichedSpec(integrationId, version);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Enable incremental updates: refetch on window focus if version not pinned
    refetchOnWindowFocus: !version,
  });

  // Poll for spec updates (if not pinned to version)
  useEffect(() => {
    if (version) return; // Don't poll if version is pinned

    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['enrichedSpec', integrationId] });
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [integrationId, version, queryClient]);

  // Extract endpoints with x-ui metadata
  const endpoints = useMemo(() => {
    if (!spec?.enrichedSpec?.paths) {
      if (__DEV__) {
        console.warn('No enriched spec paths found:', spec);
      }
      return [];
    }

    const extracted = Object.entries(spec.enrichedSpec.paths).flatMap(([path, methods]: [string, any]) =>
      Object.entries(methods).map(([method, operation]: [string, any]) => ({
        path,
        method,
        operation,
        xui: operation['x-ui'],
        responseSchema: operation.responses?.['200']?.content?.['application/json']?.schema ||
          operation.responses?.['201']?.content?.['application/json']?.schema ||
          operation.responses?.['200']?.schema
      }))
    ).filter((endpoint: any) => endpoint.xui); // Only endpoints with x-ui

    if (__DEV__) {
      console.log('Extracted endpoints:', extracted.length, extracted);
    }

    return extracted;
  }, [spec]);

  if (isLoading) {
    return (
      <YStack alignItems="center" justifyContent="center" padding={Platform.OS === 'web' ? "$6" : "$4"}>
        <Spinner size={Platform.OS === 'web' ? "large" : "small"} />
        <SizableText 
          marginTop={Platform.OS === 'web' ? "$4" : "$3"} 
          color="$gray10"
          size={Platform.OS === 'web' ? "$4" : "$3"}
        >
          Loading integration spec...
        </SizableText>
      </YStack>
    );
  }

  if (error) {
    return (
      <Card backgroundColor="$red2" padding={Platform.OS === 'web' ? "$4" : "$3"} borderRadius={12}>
        <SizableText 
          color="$red10"
          size={Platform.OS === 'web' ? "$4" : "$3"}
          lineHeight={Platform.OS === 'web' ? undefined : 18}
        >
          Failed to load integration spec: {error.message}
        </SizableText>
      </Card>
    );
  }

  if (!spec || endpoints.length === 0) {
    return (
      <Card backgroundColor="$gray2" padding={Platform.OS === 'web' ? "$4" : "$3"} borderRadius={12}>
        <SizableText 
          color="$gray10"
          size={Platform.OS === 'web' ? "$4" : "$3"}
          lineHeight={Platform.OS === 'web' ? undefined : 18}
        >
          No enriched spec available or no endpoints with x-ui metadata found.
        </SizableText>
      </Card>
    );
  }

  // Render endpoint components
  return (
    <YStack gap={Platform.OS === 'web' ? "$4" : "$3"}>
      {endpoints.map((endpoint: any, idx: number) => (
        <EndpointRenderer
          key={`${endpoint.path}-${endpoint.method}-${idx}`}
          endpoint={endpoint}
          integrationId={integrationId}
          onComponentResolved={onComponentResolved}
        />
      ))}
    </YStack>
  );
};

// Individual endpoint renderer with component resolution
const EndpointRenderer: React.FC<{
  endpoint: any;
  integrationId: string;
  onComponentResolved?: (endpoint: string, component: React.ComponentType<any>) => void;
}> = ({ endpoint, integrationId, onComponentResolved }) => {
  const [resolvedComponent, setResolvedComponent] = useState<React.ComponentType<any> | null>(null);

  // __DEV__: Initialize with mock data immediately for test integration
  const getInitialData = () => {
    if (__DEV__ && integrationId === "__test_spec_driven__") {
      if (endpoint.path === "/profile") {
        return {
          id: "test-user-123",
          name: "John Doe",
          image: "https://via.placeholder.com/150",
          email: "john.doe@example.com",
          title: "Senior Developer",
          bio: "Passionate developer working on spec-driven architecture"
        };
      } else if (endpoint.path === "/analytics/summary") {
        return {
          total: 1250,
          change: 12.5,
          period: "Last 30 days",
          metrics: {
            views: 850,
            clicks: 320,
            conversions: 80
          }
        };
      }
    }
    return null;
  };

  const [data, setData] = useState<any>(getInitialData());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resolve component on mount
  useEffect(() => {
    if (!endpoint.xui || !endpoint.responseSchema) {
      console.warn('Endpoint missing x-ui or response schema:', endpoint);
      setError('Missing x-ui metadata or response schema');
      return;
    }

    if (__DEV__) {
      console.log('Resolving component for:', {
        path: endpoint.path,
        method: endpoint.method,
        capability: endpoint.xui.capability,
        renders: endpoint.xui.renders,
        schema: endpoint.responseSchema
      });
    }

    const component = resolveComponent(endpoint.xui, endpoint.responseSchema);

    if (__DEV__) {
      console.log('Resolved component:', component ? component.name : 'null');
    }

    setResolvedComponent(component);

    if (component && onComponentResolved) {
      onComponentResolved(`${endpoint.method} ${endpoint.path}`, component);
    }

    if (!component) {
      console.warn('No matching component found for:', {
        capability: endpoint.xui.capability,
        renders: endpoint.xui.renders
      });
      setError('No matching component found');
    }
  }, [endpoint, onComponentResolved]);

  // Fetch data when component is resolved (for GET requests)
  useEffect(() => {
    if (!resolvedComponent || endpoint.method.toLowerCase() !== 'get') return;

    // __DEV__: Mock data is already set in initial state, just mark as loaded
    if (__DEV__ && integrationId === "__test_spec_driven__" && data) {
      setLoading(false);
      return;
    }

    // Only fetch if we don't have data yet
    if (data) return;

    setLoading(true);
    setError(null);

    apis.getIntegrationEndpointData(integrationId, endpoint.path, endpoint.method)
      .then((response: any) => setData(response.data || response))
      .catch((err: any) => {
        console.error('Failed to fetch endpoint data:', err);
        setError(err.message || 'Failed to fetch data');
      })
      .finally(() => setLoading(false));
  }, [resolvedComponent, endpoint, integrationId, data]);

  // Validate that resolvedComponent is actually a valid React component
  const isValidComponent = resolvedComponent && typeof resolvedComponent === 'function';

  if (!isValidComponent) {
    // Try to find fallback components
    const fallbacks = getCompatibleComponents(endpoint.xui, endpoint.responseSchema, 0.5);
    if (fallbacks.length > 0) {
      // Use the best fallback
      const FallbackComponent = fallbacks[0].component;
      if (FallbackComponent && (typeof FallbackComponent === 'function' || typeof FallbackComponent === 'object')) {
        return (
          <Card padding={Platform.OS === 'web' ? "$4" : "$3"} borderRadius={12}>
            {
              __DEV__ &&
            <SizableText 
              size={Platform.OS === 'web' ? "$3" : "$2"} 
              color="$yellow10" 
              marginBottom="$2"
              lineHeight={Platform.OS === 'web' ? undefined : 16}
            >
              Using fallback component (match score: {Math.round(fallbacks[0].match.score * 100)}%)
            </SizableText>
            }
            {endpoint.method.toLowerCase() === 'get' && data ? (
              <FallbackComponent data={data} />
            ) : (
              <FallbackComponent />
            )}
          </Card>
        );
      }
    }
    return (
      <Card backgroundColor="$red2" padding={Platform.OS === 'web' ? "$4" : "$3"} borderRadius={12}>
        <SizableText 
          color="$red10"
          size={Platform.OS === 'web' ? "$4" : "$3"}
          lineHeight={Platform.OS === 'web' ? undefined : 18}
        >
          No component match found for {endpoint.method} {endpoint.path}
        </SizableText>
        <SizableText 
          size={Platform.OS === 'web' ? "$3" : "$2"} 
          color="$red9" 
          marginTop="$2"
          lineHeight={Platform.OS === 'web' ? undefined : 16}
        >
          Capability: {endpoint.xui.capability}, Renders: {endpoint.xui.renders}
        </SizableText>
        {__DEV__ && (
          <SizableText 
            size={Platform.OS === 'web' ? "$2" : "$1"} 
            color="$red8" 
            marginTop="$2"
            lineHeight={Platform.OS === 'web' ? undefined : 14}
          >
            Resolved component type: {typeof resolvedComponent}
          </SizableText>
        )}
      </Card>
    );
  }

  if (loading && !data) {
    return (
      <YStack alignItems="center" padding={Platform.OS === 'web' ? "$4" : "$3"}>
        <Spinner size="small" />
      </YStack>
    );
  }

  if (error) {
    return (
      <Card backgroundColor="$red2" padding={Platform.OS === 'web' ? "$4" : "$3"} borderRadius={12}>
        <SizableText 
          color="$red10"
          size={Platform.OS === 'web' ? "$4" : "$3"}
          lineHeight={Platform.OS === 'web' ? undefined : 18}
        >
          {error}
        </SizableText>
      </Card>
    );
  }

  const Component = resolvedComponent as React.ComponentType<any>;

  // Render component with data (for GET) or as form (for POST/PUT)
  if (endpoint.method.toLowerCase() === 'get') {
    // Render component with data (component should handle null data gracefully)
    if (!data) {
      return (
        <Card backgroundColor="$gray2" padding={Platform.OS === 'web' ? "$4" : "$3"} borderRadius={12}>
          <SizableText 
            color="$gray10"
            size={Platform.OS === 'web' ? "$4" : "$3"}
            lineHeight={Platform.OS === 'web' ? undefined : 18}
          >
            No data available
          </SizableText>
        </Card>
      );
    }
    return <Component data={data} />;
  } else {
    // For POST/PUT, render as form (component should handle submission)
    return <Component data={data || undefined} onSubmit={(postData) => {
      console.log('Post submitted:', postData);
      // TODO: Call API to submit post
    }} />;
  }
};

export default SpecDrivenIntegrationRenderer;

